import { getTravelText, isTravelHomeSectionHidden } from "../../../components/travelTextTokens";
import React from "react";
import Link from "next/link";
import { SectionTitle, sharedStyles, placeCardStyles } from "./_shared";
import { ASSET_URL } from "../../../config";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import StarRating from "../components/StarRating";

// Grille de cards sombres pour les lieux cashers. Autonome.
export default function KosherPlacesGrid({ context }) {
    const { org, travel, lang } = context;

    const MEAT_TYPE_LABELS = {
        meat: t`Viande`,
        dairy: t`Lacté`,
        parve: t`Parvé`,
    };

    const places = (travel?.kosherPlaces || [])
        .slice()
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .slice(0, 3);

    const categories = travel?.kosher?.categories || [];
    const categoryById = React.useMemo(() => {
        const map = new Map();
        for (const c of categories) map.set(c.id, c);
        return map;
    }, [categories]);

    const cityName = org.ville || org.name;
    const gridCls = `tr-pgrid tr-pgrid--${Math.min(places.length, 4)}`;

    if (places.length === 0 || isTravelHomeSectionHidden(context, "home_kosher")) return null;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: sharedStyles + placeCardStyles }} />
            <section className="tr-ui-section">
                <SectionTitle title={getTravelText(travel, "home_kosher", t`Manger Cacher à ${cityName}`, lang, {city: cityName})} highlight={t`Manger Cacher`} />
                <div className={gridCls}>
                    {places.map((place) => {
                        const localized = applyLang(place, lang);
                        const category = place.categoryId ? categoryById.get(place.categoryId) : null;
                        const localizedCat = category ? applyLang(category, lang) : null;
                        const imgSrc = place.image ? `${ASSET_URL}${place.image}` : null;
                        const address = place.address?.address;
                        const isInactive = place.active === false;
                        const cardCls = `tr-pcard${isInactive ? " tr-pcard--inactive" : ""}`;
                        const href = org.BASE_WEB_URL + "/travel/kosher-places/" + place.id;

                        const inner = (
                            <>
                                {imgSrc ? (
                                    <img src={imgSrc} alt={localized.name} className="tr-pcard__img" loading="lazy" />
                                ) : (
                                    <div className="tr-pcard__img-placeholder">
                                        {renderIcon({ name: "IconBuildingStore", source: "tabler" }, 48)}
                                    </div>
                                )}
                                <div className="tr-pcard__wash" />
                                <div className="tr-pcard__body">
                                    {!isInactive && (
                                        <div className="tr-pcard__badges">
                                            {localizedCat?.label && (
                                                <span className="tr-pcard__badge">{localizedCat.label}</span>
                                            )}
                                            {(place.meatType || []).map(mt => (
                                                <span key={mt} className={`tr-pcard__badge tr-pcard__badge--${mt}`}>
                                                    {MEAT_TYPE_LABELS[mt] || mt}
                                                </span>
                                            ))}
                                            <StarRating value={place.note} size={13} />
                                        </div>
                                    )}
                                    <div className="tr-pcard__name">{localized.name}</div>
                                    {isInactive ? (
                                        <span className="tr-pcard__inactive-note">
                                            {renderIcon({ name: "IconAlertTriangle", source: "tabler" }, 14)}
                                            <Trans>Cet endroit n'est pas actif</Trans>
                                        </span>
                                    ) : address && (
                                        <div className="tr-pcard__meta">
                                            <span className="tr-pcard__meta-icon">
                                                {renderIcon({ name: "IconMapPin", source: "tabler" }, 13)}
                                            </span>
                                            <span>{address}</span>
                                        </div>
                                    )}
                                </div>
                            </>
                        );

                        if (isInactive) {
                            return <div key={place.id} className={cardCls}>{inner}</div>;
                        }
                        return (
                            <Link key={place.id} href={href} legacyBehavior>
                                <a className={cardCls}>{inner}</a>
                            </Link>
                        );
                    })}
                </div>

                <Link href={org.BASE_WEB_URL + "/travel/kosher-places"} legacyBehavior>
                    <a className="tr-ui-seemore">
                        <span>{t`Toute la Manger Cacher à ${cityName}`}</span>
                        <span className="tr-ui-seemore__arrow">
                            {renderIcon({ name: "IconChevronRight", source: "tabler" }, 16)}
                        </span>
                    </a>
                </Link>
            </section>
        </>
    );
}
